import React from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Typography,
  IconButton,
} from "@mhome/ui";
import { CircularProgress, useMediaQuery } from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState } from "../global-state";
import { spacingToPx } from "../lib/utils";

const NfcCheckDialog = ({
  open,
  onClose,
  checkMode,
  checkingNfc,
  checkResult,
  checkError,
  countdown,
  countdownActive,
}) => {
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isSmallScreen}
      variant={isSmallScreen ? undefined : "drawer"}
      PaperProps={{
        style: {
          width: isSmallScreen ? "100%" : "600px",
          marginTop: isSmallScreen ? 0 : `${safeAreaInsets.top}px !important`,
          marginBottom: isSmallScreen
            ? 0
            : `${safeAreaInsets.bottom}px !important`,
          height: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          maxHeight: isSmallScreen
            ? "100vh"
            : `calc(100vh - ${
                safeAreaInsets.top + safeAreaInsets.bottom
              }px) !important`,
          paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
          paddingBottom: isSmallScreen ? `${safeAreaInsets.bottom}px` : 0,
          paddingRight: `${safeAreaInsets.right}px`,
        },
      }}
    >
      <DialogTitle
        className="flex items-center gap-2"
        style={{
          minHeight: "auto",
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={checkingNfc}
          style={{
            flexShrink: 0,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography
          variant="h6"
          component="div"
          style={{
            lineHeight: 1.2,
            marginBottom: 0,
          }}
        >
          Check NFC Tag
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText style={{ marginBottom: spacingToPx(3) }}>
          {checkMode ? (
            <>Ready to check NFC tags. Scan tags to validate them.</>
          ) : (
            <>Entering check mode...</>
          )}
        </DialogContentText>

        {checkMode && (
          <>
            <DialogContentText style={{ marginBottom: spacingToPx(2) }}>
              Check mode is active. You can scan multiple tags:
            </DialogContentText>
            <ol style={{ paddingLeft: spacingToPx(2) }}>
              <Typography
                component="li"
                style={{ marginBottom: spacingToPx(1) }}
              >
                Place an NFC tag close to your device (usually on the back)
              </Typography>
              <Typography
                component="li"
                style={{ marginBottom: spacingToPx(1) }}
              >
                Keep the tag in position until you hear a sound or see a
                notification
              </Typography>
              <Typography component="li">
                The tag information will be displayed below, and you can
                continue checking more tags
              </Typography>
            </ol>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: spacingToPx(2),
                padding: spacingToPx(2),
                backgroundColor: "hsl(var(--primary))",
                color: "hsl(var(--primary-foreground))",
                borderRadius: "var(--radius)",
              }}
            >
              <SearchIcon
                style={{ marginRight: spacingToPx(2), fontSize: 24 }}
              />
              <div style={{ flex: 1 }}>
                <Typography variant="body2" style={{ fontWeight: 500 }}>
                  NFC Check Mode Active - Scan your NFC tags
                </Typography>
                {countdownActive && (
                  <Typography
                    variant="caption"
                    style={{ display: "block", marginTop: spacingToPx(0.5) }}
                  >
                    Time remaining: {Math.floor(countdown / 60)}:
                    {(countdown % 60).toString().padStart(2, "0")}
                  </Typography>
                )}
              </div>
            </div>
          </>
        )}

        {checkingNfc && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginTop: spacingToPx(2),
              padding: spacingToPx(2),
              backgroundColor: "hsl(var(--card))",
              borderRadius: "var(--radius)",
            }}
          >
            <CircularProgress
              size={20}
              style={{ marginRight: spacingToPx(2) }}
            />
            <Typography variant="body2">Entering check mode...</Typography>
          </div>
        )}

        {checkResult && (
          <div
            style={{
              marginTop: spacingToPx(2),
              padding: spacingToPx(2),
              backgroundColor: "hsl(142 76% 36% / 0.1)",
              color: "hsl(142 76% 36%)",
              borderRadius: "var(--radius)",
            }}
          >
            <Typography
              variant="h6"
              style={{ marginBottom: spacingToPx(1), fontWeight: 500 }}
            >
              Valid NFC Tag
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {checkResult}
            </Typography>
          </div>
        )}

        {checkError && (
          <div
            style={{
              marginTop: spacingToPx(2),
              padding: spacingToPx(2),
              backgroundColor: "hsl(var(--destructive) / 0.1)",
              color: "hsl(var(--destructive))",
              borderRadius: "var(--radius)",
            }}
          >
            <Typography
              variant="h6"
              style={{ marginBottom: spacingToPx(1), fontWeight: 500 }}
            >
              Check Failed
            </Typography>
            <Typography variant="body2">{checkError}</Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={checkingNfc}>
          {checkMode ? "Cancel NFC Check" : "Close"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NfcCheckDialog;
